// Attendance deduction (wage deducted for absence).
//
// Two rules that are easy to mix up here:
//
// 1. Missing attendance records are not evidence of absence. The backend
//    derives `absentDays` as a remainder (workDays - attendanceDays -
//    leaveDays), so a month with no records at all yields
//    `absentDays === actualWorkDays`: a ratio of 1, a 100% deduction. What
//    decides whether anything may be deducted is `recordedDays`, not
//    `absentDays`.
//
// 2. PP 36/2021 Article 65 caps all wage deductions at 50% of each wage
//    payment. The cap applies to that month's TOTAL deductions, not to the
//    attendance deduction alone.

export const WAGE_DEDUCTION_CEILING_RATIO = 0.5;

// `recordedDays` is a recent backend addition. While older payloads are still
// in circulation, recorded attendance and leave are enough to prove the month
// has data, and a month with neither genuinely has no records at all.
export const getRecordedDays = (summary) => {
  if (!summary) return 0;
  if (typeof summary.recordedDays === "number") return summary.recordedDays;
  return (summary.attendanceDays || 0) + (summary.leaveDays || 0);
};

export const hasAttendanceData = (summary) => getRecordedDays(summary) > 0;

// Returns the capped deduction, the figure before capping, and whether the cap
// bit. That last state is one the user needs to see, not one to hide.
export const calculateAttendanceDeduction = ({
  summary,
  baseSalary,
  otherDeductions = 0,
  isBeforeStartDate = false,
}) => {
  const wage = Number(baseSalary) || 0;
  const workDays = Number(summary?.actualWorkDays) || 0;
  const absentDays = Number(summary?.absentDays) || 0;

  const hasData = !isBeforeStartDate && hasAttendanceData(summary);

  if (!hasData || !wage || workDays <= 0 || absentDays <= 0) {
    return { amount: 0, uncappedAmount: 0, isCapped: false, hasData };
  }

  const uncappedAmount = Math.floor(wage * (absentDays / workDays));

  const ceiling = Math.floor(wage * WAGE_DEDUCTION_CEILING_RATIO);
  const remainingCeiling = Math.max(
    0,
    ceiling - (Number(otherDeductions) || 0)
  );
  const amount = Math.min(uncappedAmount, remainingCeiling);

  return {
    amount,
    uncappedAmount,
    isCapped: amount < uncappedAmount,
    hasData: true,
  };
};
